import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog.jsx';
import { Button } from './ui/button.jsx';
import { Progress } from './ui/progress.jsx';
import { Loader2, Music, CheckCircle, XCircle, Download, FileMusic } from 'lucide-react';
import api from '../services/api';

export default function MidiConversionModal({
  isOpen,
  onClose,
  sessionId,
  trackType,
  trackName,
  audioUrl,
  onComplete,
}) {
  const [status, setStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [midiUrl, setMidiUrl] = useState(null);
  const [error, setError] = useState(null);
  const [conversionId, setConversionId] = useState(null);
  const [queueStatus, setQueueStatus] = useState('starting'); // 'starting', 'queued', 'processing'

  const checkExistingConversion = useCallback(async () => {
    if (!sessionId || !trackType) return null;
    
    try {
      const response = await api.get(`/sessions/${sessionId}/midi-status/${trackType}`);
      return response.data;
    } catch (err) {
      console.error('Failed to check existing conversion:', err);
      return null;
    }
  }, [sessionId, trackType]);

  const startConversion = useCallback(async () => {
    if (!sessionId || !trackType || !audioUrl) return;
    
    setStatus('starting');
    setProgress(5);
    setError(null);
    
    try {
      const response = await api.post(`/sessions/${sessionId}/convert-to-midi`, {
        track_type: trackType,
        audio_url: audioUrl,
      });
      
      if (response.data.success) {
        setConversionId(response.data.conversion_id);
        setStatus('processing');
        setProgress(15);
      } else {
        setError(response.data.message || 'Failed to start conversion');
        setStatus('error');
      }
    } catch (err) {
      console.error('Failed to start MIDI conversion:', err);
      setError(err.response?.data?.detail || 'Failed to start conversion');
      setStatus('error');
    }
  }, [sessionId, trackType, audioUrl]);

  useEffect(() => {
    const initializeModal = async () => {
      if (!isOpen || status !== 'idle') return;
      
      const existingData = await checkExistingConversion();
      
      if (existingData && (existingData.status === 'IN_QUEUE' || existingData.status === 'PROCESSING')) {
        setStatus('processing');
        setProgress(existingData.status === 'IN_QUEUE' ? 20 : 50);
      } else if (existingData && existingData.status === 'COMPLETED' && existingData.midi_url) {
        setStatus('complete');
        setProgress(100);
        setMidiUrl(existingData.midi_url);
      } else {
        startConversion();
      }
    };
    
    initializeModal();
  }, [isOpen, status, checkExistingConversion, startConversion]);

  useEffect(() => {
    let pollInterval;
    
    if (status === 'processing' && sessionId && trackType) {
      pollInterval = setInterval(async () => {
        try {
          const response = await api.get(`/sessions/${sessionId}/midi-status/${trackType}`);
          const data = response.data;
          
          if (data.status === 'IN_QUEUE') {
            setProgress(prev => Math.min(prev + 1, 25));
            setQueueStatus('queued');
          } else if (data.status === 'PROCESSING') {
            setProgress(prev => Math.min(prev + 5, 85));
            setQueueStatus('processing');
          } else if (data.status === 'COMPLETED') {
            setProgress(100);
            setStatus('complete');
            setMidiUrl(data.midi_url);
            if (onComplete) {
              onComplete(trackType, data.midi_url);
            }
            clearInterval(pollInterval);
          } else if (data.status === 'FAILED') {
            setError(data.message || 'Conversion failed');
            setStatus('error');
            clearInterval(pollInterval);
          }
        } catch (err) {
          console.error('Error checking MIDI status:', err);
        }
      }, 3000);
    }
    
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [status, sessionId, trackType, onComplete]);

  const handleDownload = async () => {
    if (!midiUrl) return;
    
    try {
      const actualUrl = midiUrl.includes('/api/proxy/audio?url=')
        ? midiUrl
        : midiUrl;
      
      const response = await api.get(`/download-url?s3_url=${encodeURIComponent(midiUrl.replace('/api/proxy/audio?url=', ''))}`);
      const presignedUrl = response.data.download_url;
      
      const link = document.createElement('a');
      link.href = presignedUrl;
      link.download = `${trackName || trackType}.mid`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading MIDI:', error);
      const link = document.createElement('a');
      link.href = midiUrl;
      link.download = `${trackName || trackType}.mid`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleClose = () => {
    if (status !== 'processing' && status !== 'starting') {
      setStatus('idle');
      setProgress(0);
      setMidiUrl(null);
      setError(null);
      setConversionId(null);
      setQueueStatus('starting');
      onClose();
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'starting':
      case 'processing':
        return <Loader2 className="h-8 w-8 animate-spin text-primary" />;
      case 'complete':
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'error':
        return <XCircle className="h-8 w-8 text-red-500" />;
      default:
        return <Music className="h-8 w-8 text-primary" />;
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'starting':
        return 'Starting MIDI conversion...';
      case 'processing':
        if (queueStatus === 'queued') {
          return 'Waiting in queue... (this may take 1-2 minutes)';
        } else if (queueStatus === 'processing') {
          return 'Converting audio to MIDI...';
        }
        return 'Converting audio to MIDI...';
      case 'complete':
        return 'MIDI conversion complete!';
      case 'error':
        return error || 'Conversion failed';
      default:
        return 'Preparing...';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileMusic className="h-5 w-5" />
            Audio to MIDI
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-6">
          <div className="flex flex-col items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              {getStatusIcon()}
            </div>
            
            <div className="text-center">
              <p className="font-medium">{trackName || trackType}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {getStatusMessage()}
              </p>
            </div>
            
            {(status === 'starting' || status === 'processing') && (
              <div className="w-full">
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-center text-muted-foreground mt-2">
                  {progress}% complete
                </p>
              </div>
            )}
            
            {status === 'complete' && midiUrl && (
              <Button onClick={handleDownload} className="w-full">
                <Download className="h-4 w-4 mr-2" />
                Download MIDI
              </Button>
            )}
            
            {status === 'error' && (
              <div className="flex gap-2 w-full">
                <Button variant="outline" onClick={handleClose} className="flex-1">
                  Close
                </Button>
                <Button onClick={startConversion} className="flex-1">
                  Try Again
                </Button>
              </div>
            )}
            
            {status === 'complete' && (
              <Button variant="outline" onClick={handleClose} className="w-full">
                Close
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
